/**
 * service-audit client. Mirrors the controllers in
 * `apps/service-audit/src/modules/audit-consumer/`:
 *
 *   GET  /v1/audit/integrity/all          → IntegrityReportWire[]
 *   GET  /v1/audit/integrity/:chainId     → IntegrityReportWire
 *   POST /v1/audit/retention/archive[?dryRun=true]
 *   GET  /v1/audit/recent                 → AuditLogEntry[] (B3, slice 3)
 *
 * RBAC-protected:
 *   - `AuditIntegrity/verify` (AUDITOR / SYSTEM_ADMIN)
 *   - `AuditRetention/manage` (SYSTEM_ADMIN)
 *   - `AuditLog/read` (AUDITOR / SYSTEM_ADMIN — granted to dev-superadmin-role
 *     via 20260522_b3_security_admin_perms.sql)
 *
 * BigInt / seq:
 *   - `breaks[].seq` arrives as a string per the controller's `toWire` mapper
 *     (Prisma BigInt → string at the boundary). Keep it as `string` here so we
 *     never leak BigInt into JSON-serialised React state.
 */
import { env } from '../env';

import { request } from './client';

const baseUrl = (): string => env.serviceAuditBaseUrl;

export type ChainBreakKind =
  | 'HASH_MISMATCH'
  | 'PREV_HASH_MISMATCH'
  | 'GAP'
  | 'OUT_OF_ORDER'
  | 'TAMPERED';

export interface ChainBreak {
  readonly seq: string;
  readonly kind: ChainBreakKind;
  readonly expected: string | null;
  readonly actual: string | null;
}

export interface IntegrityReport {
  readonly chainId: string;
  readonly totalRows: number;
  readonly ok: boolean;
  readonly durationMs: number;
  readonly breaks: readonly ChainBreak[];
}

export async function verifyAllChains(): Promise<readonly IntegrityReport[]> {
  return request<readonly IntegrityReport[]>(baseUrl(), '/v1/audit/integrity/all', {
    method: 'GET',
  });
}

export async function verifyChain(chainId: string): Promise<IntegrityReport> {
  return request<IntegrityReport>(
    baseUrl(),
    `/v1/audit/integrity/${encodeURIComponent(chainId)}`,
    { method: 'GET' },
  );
}

export interface ArchiveError {
  readonly seq: string;
  readonly message: string;
}

export interface ArchiveReport {
  readonly processed: number;
  readonly archived: number;
  readonly skipped: number;
  readonly errors: readonly ArchiveError[];
  readonly durationMs: number;
  readonly dryRun: boolean;
}

export async function runArchivePass(opts: {
  readonly dryRun?: boolean;
  readonly batchSize?: number;
}): Promise<ArchiveReport> {
  const query: Record<string, string> = {};
  if (opts.dryRun === true) {
    query['dryRun'] = 'true';
  }
  return request<ArchiveReport>(baseUrl(), '/v1/audit/retention/archive', {
    method: 'POST',
    query,
    body: opts.batchSize !== undefined ? { batchSize: opts.batchSize } : {},
  });
}

// ---- Recent audit log (B3) ----

export interface AuditLogEntry {
  readonly id: string;
  readonly tenantId: string | null;
  readonly emittedAt: string;
  readonly receivedAt: string;
  readonly ceSource: string;
  readonly ceType: string;
  readonly subject: string | null;
  readonly correlationId: string | null;
  readonly kind: string;
  readonly module: string | null;
  readonly action: string | null;
  readonly entityType: string | null;
  readonly entityId: string | null;
  readonly actorUserId: string | null;
  readonly actorOperatorId: number | null;
  readonly chainId: string;
  readonly chainSeq: string;
}

export interface ListAuditLogsQuery {
  readonly limit?: number;
  readonly since?: string;
  readonly until?: string;
  readonly actor?: string;
  readonly module?: string;
}

export async function listRecentAuditLogs(
  q: ListAuditLogsQuery = {},
): Promise<readonly AuditLogEntry[]> {
  const query: Record<string, string | number | boolean | undefined> = {};
  if (q.limit !== undefined) query['limit'] = q.limit;
  if (q.since) query['since'] = q.since;
  if (q.until) query['until'] = q.until;
  if (q.actor) query['actor'] = q.actor;
  if (q.module) query['module'] = q.module;
  return request<readonly AuditLogEntry[]>(baseUrl(), '/v1/audit/recent', {
    method: 'GET',
    query,
  });
}
